import React, { useState, useEffect } from 'react';
import { Download, Eye, FileText, Loader2, CheckCircle, XCircle } from 'lucide-react';
import apiService from '../config';

const AllDocument = () => {
    const [currentProjectId, setCurrentProjectId] = useState(null);
    const [currentProposalId, setCurrentProposalId] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [signData, setSignData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [downloading, setDownloading] = useState(false);

    // Get Project and Proposal IDs
    useEffect(() => {
        const getProjectInfo = () => {
            const selectedProject = localStorage.getItem('selectedProject');
            if (selectedProject) {
                try {
                    const project = JSON.parse(selectedProject);
                    return project.project_id || project.id;
                } catch (e) {
                    console.error('Error parsing selectedProject:', e);
                }
            }

            const userData = localStorage.getItem('userData');
            if (userData) {
                try {
                    const user = JSON.parse(userData);
                    return user.data?.project_id || user.project_id;
                } catch (e) {
                    console.error('Error parsing userData:', e);
                }
            }
            return null;
        };

        const getProposalInfo = () => {
            const userData = localStorage.getItem('userData');
            if (userData) {
                try {
                    const user = JSON.parse(userData);
                    return user?.data?.default_proposal || user?.data?.proposal_id || user?.default_proposal || user?.proposal_id || null;
                } catch (e) {
                    console.error('Error parsing userData for proposal:', e);
                }
            }
            return null;
        };

        setCurrentProjectId(getProjectInfo());
        setCurrentProposalId(getProposalInfo());
    }, []);

    useEffect(() => {
        if (currentProjectId && currentProposalId) {
            fetchDocuments();
        }
    }, [currentProjectId, currentProposalId]);

    const fetchDocuments = async () => {
        if (currentProjectId == null || currentProposalId == null) return;

        setLoading(true);
        setError(null);

        try {
            const result = await apiService.getAllDocuments(
                currentProjectId,
                currentProposalId
            );

            console.log('📄 API Result:', result);

            if (result?.status === 200) {
                setDocuments(result.data || []);
                setSignData(result.signData || []);
                console.log('✅ Documents loaded:', result.data?.length || 0);
                console.log('✅ Signed sections loaded:', result.signData?.length || 0);
            } else if (result?.status === 0) {
                setError(result.message || "Missing required parameters");
            } else {
                setError("Failed to fetch documents");
            }
        } catch (err) {
            console.error('❌ Error fetching documents:', err);
            setError(err.message || "Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    const handleViewDocument = (doc) => {
        setSelectedDocument(doc);
    };

    const closeModal = () => {
        setSelectedDocument(null);
    };

    // ✅ WORKING DOWNLOAD ZIP FUNCTION
    // Add this downloadZip function to replace your existing one

    const downloadZip = async () => {
        try {
            setDownloading(true);

            const response = await apiService.getAllDocumentsZip(
                currentProjectId,
                currentProposalId
            );

            const blob = new Blob(
                [response.data],
                { type: 'application/zip' }
            );

            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = `documents_project_${currentProjectId}_proposal_${currentProposalId}.zip`;

            document.body.appendChild(link);
            link.click();

            link.remove();
            window.URL.revokeObjectURL(url);

        } catch (error) {
            console.error('❌ Download error:', error);

            if (error.code === 'ECONNABORTED') {
                alert('Download timeout. File is too large.');
            } else {
                alert('Download failed. Please try again.');
            }
        } finally {
            setDownloading(false);
        }
    };

    const getFileType = (url) => {
        const extension = url.split('.').pop().toLowerCase().split('?')[0];
        if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) {
            return 'image';
        } else if (['pdf'].includes(extension)) {
            return 'pdf';
        }
        return 'other';
    };

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <div className="max-w-7xl mx-auto">
                <div className="bg-white rounded-xl shadow-md">
                    {/* Header */}
                    <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                        <div>
                            <h2 className="text-2xl font-semibold text-gray-800">
                                All Documents
                            </h2>
                            <p className="text-sm text-gray-500 mt-1">
                                Project ID: {currentProjectId} | Proposal ID: {currentProposalId}
                            </p>
                            {documents.length > 0 && (
                                <p className="text-sm text-blue-600 mt-1">
                                    📄 Total Documents: {documents.length} | 📝 Signed Sections: {signData.length}
                                </p>
                            )}
                        </div>
                        <button
                            onClick={downloadZip}
                            disabled={downloading || (documents.length === 0 && signData.length === 0)}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                        >
                            {downloading ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    <span>Downloading ZIP...</span>
                                </>
                            ) : (
                                <>
                                    <Download className="w-5 h-5" />
                                    Download All as ZIP
                                </>
                            )}
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                        {loading ? (
                            <div className="flex justify-center items-center py-12">
                                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                            </div>
                        ) : error ? (
                            <div className="text-center py-12">
                                <p className="text-red-600">{error}</p>
                                <button
                                    onClick={fetchDocuments}
                                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                >
                                    Retry
                                </button>
                            </div>
                        ) : documents.length === 0 && signData.length === 0 ? (
                            <div className="text-center py-12">
                                <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-600">No documents found</p>
                            </div>
                        ) : (
                            <>
                                {/* Signed Sections Table */}
                                {signData.length > 0 && (
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-800 mb-4">📝 Signed Sections</h3>
                                        <div className="overflow-x-auto">
                                            <table className="w-full">
                                                <thead className="bg-gray-50 border-b border-gray-200">
                                                    <tr>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Sr No</th>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Section Type</th>
                                                        {/* <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">File Path</th> */}
                                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase">Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-200">
                                                    {signData.map((sign, index) => (
                                                        <tr key={index} className="hover:bg-gray-50">
                                                            <td className="px-4 py-4 text-sm text-gray-900">{index + 1}</td>
                                                            <td className="px-4 py-4 text-sm text-gray-900 font-medium">{sign.section_type || '-'}</td>
                                                            {/* <td className="px-4 py-4 text-sm text-gray-600 truncate max-w-md">{sign.file_path}</td> */}
                                                            <td className="px-4 py-4 text-center">
                                                                <button
                                                                    onClick={() => handleViewDocument({ ...sign, stored_file_name: sign.section_type, upload_path: sign.file_path })}
                                                                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                                                                >
                                                                    <Eye className="w-4 h-4" />
                                                                    View
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                                {/* Tracking Documents Table */}
                                {documents.length > 0 && (
                                    <div className="mb-8">
                                        <h3 className="text-lg font-semibold text-gray-800 mb-4">📄 Tracking Documents</h3>
                                        <div className="overflow-x-auto">
                                            <table className="w-full">
                                                <thead className="bg-gray-50 border-b border-gray-200">
                                                    <tr>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Sr No</th>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">ID</th>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Document Name</th>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">From Stage</th>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">To Stage</th>
                                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase">Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-200">
                                                    {documents.map((doc, index) => (
                                                        <tr key={doc.document_id} className="hover:bg-gray-50">
                                                            <td className="px-4 py-4 text-sm text-gray-900">{index + 1}</td>
                                                            <td className="px-4 py-4 text-sm text-gray-900">{doc.document_id}</td>
                                                            <td className="px-4 py-4 text-sm text-gray-900 font-medium">{doc.stored_file_name}</td>
                                                            <td className="px-4 py-4 text-sm text-gray-600">{doc.from_stage_name || '-'}</td>
                                                            <td className="px-4 py-4 text-sm text-gray-600">{doc.to_stage_name || '-'}</td>
                                                            <td className="px-4 py-4 text-center">
                                                                <button
                                                                    onClick={() => handleViewDocument(doc)}
                                                                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                                                                >
                                                                    <Eye className="w-4 h-4" />
                                                                    View
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Document Viewer Modal */}
            {selectedDocument && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                            <h3 className="text-lg font-semibold text-gray-800">
                                {selectedDocument.stored_file_name}
                            </h3>
                            <button
                                onClick={closeModal}
                                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                            >
                                ×
                            </button>
                        </div>

                        <div className="flex-1 overflow-auto p-4 bg-gray-100">
                            {getFileType(selectedDocument.upload_path) === 'image' ? (
                                <img
                                    src={selectedDocument.upload_path}
                                    alt={selectedDocument.stored_file_name}
                                    className="max-w-full h-auto mx-auto rounded-lg shadow-lg"
                                />
                            ) : getFileType(selectedDocument.upload_path) === 'pdf' ? (
                                <iframe
                                    src={selectedDocument.upload_path}
                                    className="w-full h-full min-h-[600px] rounded-lg"
                                    title={selectedDocument.stored_file_name}
                                />
                            ) : (
                                <div className="text-center py-12">
                                    <p className="text-gray-600 mb-4">Preview not available for this file type</p>
                                    <a
                                        href={selectedDocument.upload_path}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                    >
                                        <Download className="w-4 h-4" />
                                        Download File
                                    </a>
                                </div>
                            )}
                        </div>

                        <div className="p-4 border-t border-gray-200 bg-gray-50">
                            <div className="text-sm text-gray-600">
                                {selectedDocument.document_id && <p><strong>Document ID:</strong> {selectedDocument.document_id}</p>}
                                {selectedDocument.from_stage_name && <p><strong>From:</strong> {selectedDocument.from_stage_name}</p>}
                                {selectedDocument.to_stage_name && <p><strong>To:</strong> {selectedDocument.to_stage_name}</p>}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AllDocument;